import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import UserService from '../services/UserService';
import { isMobile, isTablet } from 'react-device-detect';
import util from '../utils/util';

/**
 * Custom hook for logging user activity in React applications
 * @param {Object} options - Configuration options
 * @param {Array} options.events - Events to capture (default: ['click', 'submit', 'change', 'input'])
 * @param {boolean} options.logToConsole - Whether to log events to console for debugging
 * @param {boolean} options.captureValues - Whether to capture input values (caution: privacy implications)
 * @param {boolean} options.captureElementInfo - Whether to capture detailed element information
 * @param {number} options.throttleTime - Throttle time in milliseconds
 * @returns {Object} - Methods to manually log events and set context
 */
const useActivityLogger = ({
  events = ['click', 'submit', 'change', 'input'],
  logToConsole = false,
  captureValues = false,
  captureElementInfo = true,
  throttleTime = 1000
} = {}) => {
  const location = useLocation();
  const [ip, setIp] = useState('Unknown');
  const [sessionId, setSessionId] = useState('');
  const currentModule = useRef('');
  const lastLogTime = useRef({});
  const ipFetchAttempted = useRef(false);

  // Generate session ID
  useEffect(() => {
    const newSessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setSessionId(newSessionId);
    
    if (logToConsole) {
      console.log('Activity Logger Session ID:', newSessionId);
    }
  }, [logToConsole]);

  // Fetch IP with multiple fallbacks and error handling
  useEffect(() => {
    if (ipFetchAttempted.current) return;
    ipFetchAttempted.current = true;

    const fetchIP = async () => {
      // Method 1: Try api.ipify.org (most reliable with CORS)
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

        const response = await fetch('https://api.ipify.org?format=json', {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data.ip) {
            setIp(data.ip);
            if (logToConsole) {
              console.log('IP Address detected:', data.ip);
            }
            return;
          }
        }
      } catch (error) {
        if (logToConsole) {
          console.warn('Primary IP service unavailable, trying fallback...');
        }
      }

      // Method 2: Try ipapi.co (original service)
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        const response = await fetch('https://ipapi.co/json/', {
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data.ip) {
            setIp(data.ip);
            if (logToConsole) {
              console.log('IP Address detected (fallback):', data.ip);
            }
            return;
          }
        }
      } catch (error) {
        if (logToConsole) {
          console.warn('Secondary IP service unavailable');
        }
      }

      // Method 3: Use server-side detection if available
      try {
        const response = await fetch('/api/get-client-ip', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.ip) {
            setIp(data.ip);
            if (logToConsole) {
              console.log('IP Address detected (server-side):', data.ip);
            }
            return;
          }
        }
      } catch (error) {
        // Server endpoint not available, continue to fallback
      }

      // Method 4: Generate local identifier as fallback
      const localId = `Local-${sessionId.substring(0, 8)}`;
      setIp(localId);
      
      if (logToConsole) {
        console.log('IP detection failed, using local identifier:', localId);
        console.log('This is normal in development or when CORS is restricted');
      }
    };

    // Don't block the application - fetch IP asynchronously
    fetchIP().catch(error => {
      if (logToConsole) {
        console.warn('IP detection error (non-critical):', error.message);
      }
      setIp(`Local-${sessionId.substring(0, 8)}`);
    });
  }, [sessionId, logToConsole]);

  // Update module based on route
  useEffect(() => {
    const path = location.pathname;
    const moduleName = path.split('/')[1] || 'Dashboard';
    currentModule.current = moduleName.charAt(0).toUpperCase() + moduleName.slice(1);
    
    if (logToConsole) {
      console.log('Current Module:', currentModule.current);
    }
  }, [location, logToConsole]);

  const createLog = (event, eventType, customDetails = {}) => {
    try {
      const currentTime = new Date();
      currentTime.setHours(currentTime.getHours() + 2); // SAST timezone adjustment

      const deviceType = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';

      const log = {
        user: util.getLoggedName() || 'Anonymous',
        eventType: eventType || (event && event.type) || 'custom',
        timestamp: currentTime.toISOString(),
        ipAddress: ip,
        sessionId: sessionId,
        deviceType: deviceType,
        userAgent: navigator.userAgent,
        url: window.location.href,
        pathname: window.location.pathname,
        module: currentModule.current,
        details: {
          ...customDetails
        }
      };

      // Capture event details if available
      if (event && captureElementInfo) {
        // Mouse coordinates
        if (event.clientX !== undefined && event.clientY !== undefined) {
          log.details.clickPosition = {
            x: event.clientX,
            y: event.clientY
          };
        }

        // Keyboard info
        if (event.key) {
          log.details.key = event.key;
        }

        // Element information
        if (event.target) {
          log.details.element = {
            type: event.target.tagName?.toLowerCase(),
            id: event.target.id || null,
            name: event.target.name || null,
            className: event.target.className || null
          };

          // Capture input values only if explicitly enabled
          if (captureValues && event.target.value !== undefined) {
            log.details.element.value = event.target.value;
          }

          // Form submission details
          if (event.type === 'submit' && event.target.tagName === 'FORM') {
            log.details.form = {
              id: event.target.id || null,
              name: event.target.name || null,
              action: event.target.action || null
            };
          }
        }
      }

      // Additional context
      log.details.viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      };

      log.details.online = navigator.onLine;

      if (logToConsole) {
        console.log('Activity Log:', {
          event: log.eventType,
          module: log.module,
          user: log.user,
          ip: log.ipAddress,
          device: log.deviceType,
          details: log.details
        });
      }

      // Send to backend (with error handling)
      UserService.logActivity(log)
        .then(response => {
          if (logToConsole) {
            console.log('Log saved successfully:', response.data);
          }
        })
        .catch(error => {
          // Only log to console if debugging is enabled
          if (logToConsole) {
            console.error('Error saving log (non-critical):', error.message);
          }
          
          // Optionally store failed logs in localStorage for retry
          try {
            const failedLogs = JSON.parse(localStorage.getItem('failedActivityLogs') || '[]');
            failedLogs.push({ log, error: error.message, timestamp: new Date().toISOString() });
            
            // Keep only last 50 failed logs
            if (failedLogs.length > 50) {
              failedLogs.shift();
            }
            
            localStorage.setItem('failedActivityLogs', JSON.stringify(failedLogs));
          } catch (storageError) {
            // If localStorage fails, just ignore
          }
        });
    } catch (error) {
      if (logToConsole) {
        console.error('Error creating activity log:', error);
      }
    }
  };

  const throttledLog = (event, eventType, customDetails) => {
    try {
      const now = Date.now();
      const eventKey = eventType || (event && event.type) || 'custom';

      if (!lastLogTime.current[eventKey] || now - lastLogTime.current[eventKey] > throttleTime) {
        lastLogTime.current[eventKey] = now;
        createLog(event, eventType, customDetails);
      }
    } catch (error) {
      if (logToConsole) {
        console.error('Error in throttled log:', error);
      }
    }
  };

  // Setup event listeners
  useEffect(() => {
    if (!events || events.length === 0) return;
    if (!sessionId) return; // Wait for session ID to be generated

    const handleEvent = (event) => {
      try {
        throttledLog(event);
      } catch (error) {
        if (logToConsole) {
          console.error('Error handling event:', error);
        }
      }
    };

    // Add event listeners
    events.forEach(eventType => {
      window.addEventListener(eventType, handleEvent, { passive: true });
    });

    // Log initial page view
    throttledLog(null, 'Page Viewed', { 
      pageUrl: window.location.href,
      referrer: document.referrer || 'Direct'
    });

    // Cleanup
    return () => {
      events.forEach(eventType => {
        window.removeEventListener(eventType, handleEvent);
      });
    };
  }, [events, ip, sessionId, throttleTime, logToConsole]);

  // Retry failed logs periodically
  useEffect(() => {
    const retryInterval = setInterval(() => {
      try {
        const failedLogs = JSON.parse(localStorage.getItem('failedActivityLogs') || '[]');
        
        if (failedLogs.length > 0 && navigator.onLine) {
          const logToRetry = failedLogs[0];
          
          UserService.logActivity(logToRetry.log)
            .then(() => {
              // Remove successfully sent log
              failedLogs.shift();
              localStorage.setItem('failedActivityLogs', JSON.stringify(failedLogs));
              
              if (logToConsole) {
                console.log('Retry successful for failed log');
              }
            })
            .catch(() => {
              // Keep in queue for next retry
            });
        }
      } catch (error) {
        // Ignore retry errors
      }
    }, 30000); // Retry every 30 seconds

    return () => clearInterval(retryInterval);
  }, [logToConsole]);

  return {
    /**
     * Manually log an event
     * @param {string} eventType - Type of event (e.g., 'Button Click', 'Form Submit')
     * @param {object} details - Additional details about the event
     */
    logEvent: (eventType, details) => {
      throttledLog(null, eventType, details);
    },

    /**
     * Set the current module/section name
     * @param {string} newModule - Name of the module
     */
    setModule: (newModule) => {
      currentModule.current = newModule;
      if (logToConsole) {
        console.log('Module updated to:', newModule);
      }
    },

    /**
     * Get the current session ID
     * @returns {string} Session ID
     */
    getSessionId: () => sessionId,

    /**
     * Get the detected IP address
     * @returns {string} IP address or local identifier
     */
    getIpAddress: () => ip,

    /**
     * Get failed logs count
     * @returns {number} Number of failed logs in queue
     */
    getFailedLogsCount: () => {
      try {
        const failedLogs = JSON.parse(localStorage.getItem('failedActivityLogs') || '[]');
        return failedLogs.length;
      } catch {
        return 0;
      }
    },

    /**
     * Clear failed logs queue
     */
    clearFailedLogs: () => {
      try {
        localStorage.removeItem('failedActivityLogs');
        if (logToConsole) {
          console.log('Failed logs cleared');
        }
      } catch (error) {
        console.error('Error clearing failed logs:', error);
      }
    }
  };
};

export default useActivityLogger;